import { Pano } from "./Pano.js";
import { Marker2d } from "./Marker2d.js";
import { VOMarker } from "../model/Structure.js";

export class View3d {

    public onRender = e5.Signal.create();
    public orbit: e5.Orbit;
    public element: HTMLCanvasElement;

    private _renderer: THREE.WebGLRenderer;
    private _scene: THREE.Scene;
    private _camera: THREE.PerspectiveCamera;
    private _material: THREE.MeshBasicMaterial;
    private _videoTexture: THREE.VideoTexture;
    private _posterTexture: THREE.Texture;
    private _raycaster: THREE.Raycaster = new THREE.Raycaster();
    private _matrix: THREE.Matrix4 = new THREE.Matrix4();
    private _hPixelToDegree: number = 0;
    private _vPixelToDegree: number = 0;
    private _visible: boolean = true;
    private _sphere: THREE.Mesh;
    private _viewBounds: ClientRect = null;

    constructor(public pano: Pano) {
        this.element = pano.element.querySelector(".three-canvas");

        this._scene = new THREE.Scene();
        this._camera = new THREE.PerspectiveCamera(100);
        this._camera.position.z = -0.1;
        this._scene.add(this._camera);

        this._renderer = new THREE.WebGLRenderer({
            preserveDrawingBuffer: false,
            alpha: false,
            antialias: true,
            stencil: false,
            canvas: this.element
        });

        this._videoTexture = new THREE.VideoTexture(this.pano.video);
        this._videoTexture.minFilter = THREE.LinearFilter;
        this._videoTexture.format = THREE.RGBFormat;

        this._material = new THREE.MeshBasicMaterial({ map: this._videoTexture });
        var geometry = new THREE.SphereGeometry(10, 16, 16);
        geometry.scale(-1, 1, 1);
        var object = new THREE.Mesh(geometry, this._material);
        this._scene.add(object);
        this._sphere = object;

        this.setCameraControl();

        this.pano.onResize.add(() => this.resize());

        this.pano.onChangeVideo.add((d) => {
            // this._videoTexture.needsUpdate = true;
            // this._material.needsUpdate = true;
            if (!this.pano.options.debug && d && d.look_at) {
                this.lookAt(d.camera_x, d.camera_y);
            }
        });

        this.resize();
    }

    public get scene(): THREE.Scene {
        return this._scene;
    }

    public get renderer(): THREE.WebGLRenderer {
        return this._renderer;
    }

    private lookAt(x: number, y: number, time: number = 0): void {
        var lng = -x * 360 + 180;
        var lat = y * 180 - 90;
        this.orbit.tween(lat, lng, time);
    }

    public lookAtMarker(marker: VOMarker, time: number = 1): void {
        //Get horizontal and vertical field-of-view in degrees.
        var vfov = this._camera.fov * Math.PI / 180;
        var hfov = 2 * Math.atan(Math.tan(vfov / 2) * this._camera.aspect) * 180 / Math.PI;
        vfov *= 180 / Math.PI;

        //Create lat/lng offset based on 2d pixel coordinates.
        var offLng = 0;
        var offLat = 0;
        if (marker.marker_action === "text") {
            offLng = ((marker.marker2d.width * 0.5) / this._viewBounds.width) * hfov;
            offLat = (marker.marker2d.height / this._viewBounds.height) * -vfov;
        }
        else {
            offLng = ((marker.marker2d.width * 0.5) / this._viewBounds.width) * -hfov;
        }

        //Calc lat/lng from uv-values.
        var lng = -marker.marker_x * 360 + 180;
        var lat = marker.marker_y * 180 - 90;
        this.orbit.tween(lat + offLat, lng + offLng, time);
    }

    public showPoster(url: string): void {
        if(this._posterTexture)
            this._posterTexture.dispose();
        this._posterTexture = new THREE.TextureLoader().load(url);
        this._material.map = this._posterTexture;
        this._material.needsUpdate = true;
    }

    public hidePoster(): void {
        this._material.map = this._videoTexture;
        this._material.needsUpdate = true;
    }

    private get fov(): number {
        return this._camera.fov;
    }

    private set fov(v: number) {
        v = e5.Calc.clamp(v, 50, 100);
        if (this._camera.fov === v)
            return;
        this._camera.fov = v;
        this._camera.updateProjectionMatrix();
        this.pano.onChangeCameraFov.dispatch();
        this.updatePixelRatio();
    }

    private resize(): void {
        this._viewBounds = this.pano.viewContainer.getBoundingClientRect();
        var bounds = this.bounds;
        var w = bounds.width;
        var h = bounds.height;
        // this._renderer.setViewport(0, 0, w, h);
        this._renderer.setSize(w, h, false);
        this._camera.aspect = w / h;
        this._camera.updateProjectionMatrix();

        this.updatePixelRatio();
    }

    private updatePixelRatio(): void {
        //Calc pixel to degree/radian transformation.
        var bounds = this.bounds;
        var w = bounds.width;
        var h = bounds.height;
        var vfov = this._camera.fov * Math.PI / 180;
        var hfov = 2 * Math.atan(Math.tan(vfov / 2) * this._camera.aspect);
        this._hPixelToDegree = (hfov / w);
        this._vPixelToDegree = (vfov / h);
        this.orbit.dragSpeedX = this._hPixelToDegree * 100;
        this.orbit.dragSpeedY = this._vPixelToDegree * 100;
    }

    private setCameraControl(): void {
        var updateFov = (e: Event, delta: number) => {
            if (!this.visible)
                return;
            this.fov = this.fov + delta;
            e.preventDefault();
        };
        this.pano.viewContainer.addEventListener("mousewheel", (e: any) => updateFov(e, -(e.wheelDelta / 40)), {passive: false});
        this.pano.viewContainer.addEventListener("DOMMouseScroll", (e) => updateFov(e, (<any>e).detail), {passive: false});

        var getTouchScale = (touches: TouchList): number => {
            if (touches.length < 2)
                return 0;
            var startX = touches[0].clientX;
            var startY = touches[0].clientY;
            var endX = touches[1].clientX;
            var endY = touches[1].clientY;
            var dx = endX - startX;
            var dy = endY - startY;
            return Math.sqrt(dx * dx + dy * dy);
        };

        //Pinch zoom.
        var dist = 0;
        var scaling = true;

        var touchStart = (e: TouchEvent) => {
            if (!this.visible || e.touches.length < 2)
                return;
            scaling = true;
            dist = getTouchScale(e.touches);
        };

        var touchMove = (e: TouchEvent) => {
            if (!this.visible || e.touches.length < 2)
                return;
            var ndist = getTouchScale(e.touches);
            var scale = ndist - dist;
            this.fov -= scale * 0.25;
            dist = ndist;
            
        };

        var touchEnd = (e: TouchEvent) => {
            if (!scaling || e.touches.length != 1)
                return;
            scaling = false;
        };

        this.pano.viewContainer.addEventListener("touchstart", touchStart, {passive: true});
        this.pano.viewContainer.addEventListener("touchmove", touchMove, {passive: true});
        this.pano.viewContainer.addEventListener("touchend", touchEnd, {passive: true});
        this.pano.viewContainer.addEventListener("touchcancel", touchEnd, {passive: true});

        this.pano.viewContainer.style.touchAction = "none";

        this.orbit = new e5.Orbit(this._camera, this.pano.viewContainer);
        this.orbit.stopPropagation = true;
        this.orbit.invertX = true;
        this.orbit.invertY = true;
        // this.orbit.onStart.add(() => console.log("START"));
        // this.orbit.onEnd.add(() => console.log("END"));
        this.orbit.onChange.add(() => {
            this.pano.onChangeCameraDirection.dispatch();
        });
    }

    private hitTest(clientX: number, clientY: number, elements: THREE.Object3D[], recursive: boolean = false): THREE.Intersection {
        //Transform to NDC (Normal Device Coordinates).
        var bounds = this.bounds;
        var local = this.toLocal(clientX, clientY);
        var x = (local.x / bounds.width) * 2 - 1;
        var y = - (local.y / bounds.height) * 2 + 1;
        this._raycaster.setFromCamera({ x: x, y: y }, this._camera);
        var intersects = this._raycaster.intersectObjects(elements, recursive);
        if (intersects.length > 0)
            return intersects[0];
        return null;
    }

    public toPosition(clientX: number, clientY: number): { x: number; y: number } {
        var hit: any = this.hitTest(clientX, clientY, [this._sphere]);
        if (hit) {
            return { x: hit.uv.x, y: 1 - hit.uv.y };
        }
        return null;
    }

    public getMarkerPosition(marker: Marker2d): { x: number; y: number, isInView: boolean } {
        return this.toGlobal(marker.position3d);
    }

    private toGlobal(position: THREE.Vector3): { x: number; y: number, isInView: boolean } {
        var pt = position.clone();
        pt.applyMatrix4(this._camera.matrixWorldInverse);
        var isInView = true;
        // if (pt.z > 0)
        //     isInView = false;
        if (pt.z > 0)
            return null;

        var bounds = this.bounds;
        var pos = position.clone();
        var matrix = this._matrix;
        matrix.multiplyMatrices(this._camera.projectionMatrix, this._camera.matrixWorldInverse);
        pos.applyMatrix4(matrix);

        var x = (pos.x + 1) * bounds.width * 0.5;
        var y = (-pos.y + 1) * bounds.height * 0.5;
        if (isInView) {
            // var margin = 200;
            // if (x < -margin || x > bounds.width + margin || y < -margin || y > bounds.height + margin) {
            //     isInView = false;
            // }
        }

        if (!isInView) {
            // var pos = position.clone();
            // var matrix = this._matrix;
            // matrix.multiplyMatrices(this._camera.projectionMatrix, this._camera.matrixWorldInverse);
            // pos.applyMatrix4(matrix);

            // var x = (pos.x + 1) * bounds.width * 0.5;
            // var y = (-pos.y + 1) * bounds.height * 0.5;
        }

        return { x: x, y: y, isInView: isInView };
    }

    private toLocal(clientX: number, clientY: number): { x: number; y: number } {
        var bounds = this.bounds;
        var localX = clientX - bounds.left;
        var localY = clientY - bounds.top;
        return { x: localX, y: localY };
    }

    private get bounds(): ClientRect {
        return this._viewBounds;
        // return this.pano.viewContainer.getBoundingClientRect();
    }

    public get camera(): THREE.PerspectiveCamera {
        return this._camera;
    }

    public update(): void {
        if (!this._visible)
            return;
        if (!this._renderer.vr.enabled) {
            this.orbit.update();
        }
        this._renderer.render(this._scene, this._camera);
        this.onRender.dispatch();
    }

    public get visible(): boolean {
        return this._visible;
    }

    public set visible(v: boolean) {
        if (this._visible === v)
            return;
        this._visible = v;
        this.orbit.enabled = v;
        this._renderer.domElement.classList.toggle("hidden", !v);
    }
}